import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

export default function PurchaseDialog({ event, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", dob: "" });
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (!event) return null;
  const total = (event.price * qty).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.dob) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      if (event.is_free) {
        const { data } = await api.post("/tickets/free", { event_id: event.id, ...form, quantity: qty });
        toast.success("You're on the list! Check your email.");
        nav(`/ticket/${data.tickets[0].qr_token}`);
      } else {
        const { data } = await api.post("/payments/checkout", {
          event_id: event.id,
          ...form,
          quantity: qty,
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-brand-ink/20 py-3 outline-none focus:border-brand-orange transition-colors placeholder:text-brand-ink/30";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-brand-ink/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
        onClick={onClose}
        data-testid="purchase-dialog"
      >
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
          className="bg-brand-cream w-full md:max-w-lg max-h-[92vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <img src={event.cover_image_url} alt={event.title} className="w-full h-40 object-cover" />
            <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white flex items-center justify-center" data-testid="close-dialog">
              <X size={18} />
            </button>
          </div>

          <div className="p-6 md:p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-orange mb-1">
              {event.date} · {event.time} · {event.city}
            </div>
            <h3 className="font-head font-extrabold text-3xl uppercase leading-none">{event.title}</h3>
            <p className="text-brand-ink/50 text-sm mt-1">{event.venue}</p>

            <form onSubmit={submit} className="mt-7 space-y-5">
              <input className={field} placeholder="Full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="buyer-name" />
              <input className={field} type="email" placeholder="Email address" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="buyer-email" />
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-ink/40">Date of birth</label>
                <input className={field} type="date" value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="buyer-dob" />
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-ink/60">Quantity</span>
                <div className="flex items-center gap-4">
                  <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 border border-brand-ink/20 flex items-center justify-center hover:border-brand-orange" data-testid="qty-minus">
                    <Minus size={14} />
                  </button>
                  <span className="font-head font-bold text-xl w-6 text-center" data-testid="qty-value">{qty}</span>
                  <button type="button" onClick={() => setQty((q) => Math.min(10, q + 1))} className="w-9 h-9 border border-brand-ink/20 flex items-center justify-center hover:border-brand-orange" data-testid="qty-plus">
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="confirm-purchase"
                className="w-full mt-4 rounded-full bg-brand-orange text-white py-4 font-mono text-sm uppercase tracking-[0.2em] hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : event.is_free ? "Confirm RSVP" : `Pay $${total} →`}
              </button>
              {!event.is_free && (
                <p className="text-center text-brand-ink/40 text-xs">Secure checkout · test card 4242 4242 4242 4242</p>
              )}
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
